import { constructDefinitionSourceFromBase } from '../port/k1-compile/from-base/definition-source';
import { createFormulaReadContext } from './formula-read-context';
import { constructPlayerDefinitionPictures } from './from-base-player-media';
import { readItemField } from './item-field-reader';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { canonicalizeCreatureSizeCells, TINY_CREATURE_SIZE_ID } from './creature-size';
import { appendEnvironmentalSource, cleanEnvironmentalSource } from './inventory-environment';
import { objectVitalityMax } from './object-vitality';
import { vitalityScaleOf } from '../inventory/transform-edit';
import { phpJsonTruthy, phpJsonString } from './php-json-values';
import { createEnchantmentTemplateBuilder } from './enchantment-template';
import { fromBaseRecord, fromBaseUuid, freezeFromBaseRow, memoFromBaseRead, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { FromBaseInventoryMetadata } from './from-base-inventory';
import { fromBaseEnchantmentMembers, applyFromBaseIntrinsicEnchantment } from './from-base-enchantment-rows';

/** One source graph per compile. Provider queries construct only this window;
 * no computed blob, module memo or another bundle's state enters the graph. */
export function createClassificationContext(base: Row, metadata: FromBaseInventoryMetadata,
    fullDefinition?: (id: string) => Row | null) {
    const diagnostics: string[] = [];
    const catalog = fromBaseRecord(base.catalog) ? base.catalog : {};
    const raw = memoFromBaseRead(catalog, 'classification-catalog-index', () => {
        const raw = new Map<string, Row>();
        for (const [id, source] of Object.entries(catalog)) {
            if (!fromBaseUuid(id) || !fromBaseRecord(source)
                || String(readItemField(source, 'item_id')).toUpperCase() !== id.toUpperCase())
                throw new TypeError(`Invalid raw classification definition: ${id}`);
            raw.set(id.toUpperCase(), source);
        }
        return raw;
    });
    const missing = new Set((base.missing_definition_ids as readonly string[] | undefined ?? []).map(id => id.toUpperCase()));
    const rawDefinition = (id: string): Row | null => {
        const source = raw.get(id.toUpperCase());
        if (source) return source;
        if (missing.has(id.toUpperCase())) return null;
        throw new FromBaseInputMissing('definition', id);
    };
    const prepared = new Map<string, Row>(), preparing = new Set<string>();
    const definition = (id: string): Row | null => {
        const key = id.toUpperCase();
        const cached = prepared.get(key);
        if (cached) return cached;
        const source = rawDefinition(id);
        if (!source) return null;
        const row = constructDefinitionSourceFromBase(source);
        if (preparing.has(key)) throw new FromBaseStageMissing('classification-template-swarm-media', `definition-cycle:${id}`);
        preparing.add(key);
        try {
            const pictures = constructPlayerDefinitionPictures(source, metadata.cdnUrl, readers.layerValue);
            if (pictures.length) row.pictures = { item: pictures };
            const result = classificationTypes.has(String(readItemField(row, 'item_type')))
                ? intrinsicEnchantment(dynamicClassification(row)) : row;
            prepared.set(key, freezeFromBaseRow(result));
            return result;
        } finally { preparing.delete(key); }
    };
    const readers = createFormulaReadContext({ catalogRows: metadata.layerCatalog, abilityTypes: metadata.abilityTypes,
        aggregationStrategy: name => metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: definition,
        resolveInventoryInstance: id => { throw new FromBaseInputMissing('inventory', id); } });
    const ownReaders = createFormulaReadContext({ catalogRows: {}, abilityTypes: metadata.abilityTypes,
        aggregationStrategy: () => 'host_wins', resolveCarrierRef: () => null,
        resolveInventoryInstance: id => { throw new FromBaseInputMissing('inventory', id); } });
    const classificationTypes = new Set([...metadata.objectTypes, 'creature', 'lineage', 'map_marker',
        'creature_template', 'creature_sub_type', 'creature_ancestry', 'creature_grouping', 'creature_lineage',
        'creature_sub_type_ancestral']);
    const hasLayer = (target: Parameters<typeof readers.layerValue>[0], name: string) => target !== null
        && (readers.layerValue(target, name) !== null || phpJsonTruthy(readers.layerEntries(target, name)));
    let activeEnchantmentItem = '';
    const buildTemplate = createEnchantmentTemplateBuilder({ ...readers, hasLayer,
        dynamicLayers: metadata.dynamicLayers, spellCosts: metadata.spellCosts,
        templateRows: id => {
            if (!Array.isArray(base.enchantment_templates)) throw new FromBaseInputMissing('enchantment-source-rows', id);
            return (base.enchantment_templates as Row[]).filter(entry =>
                phpJsonString(readItemField(entry, 'enchantment_template_id')).toUpperCase() === id.toUpperCase());
        },
        memberRows: id => {
            if (!Array.isArray(base.enchantment_template_enchantments)) throw new FromBaseInputMissing('enchantment-source-rows', id);
            return fromBaseEnchantmentMembers(base.enchantment_template_enchantments as Row[], id, rawDefinition);
        },
        fullItem: id => fullDefinition && !preparing.has(id.toUpperCase()) ? fullDefinition(id) : definition(id),
        unresolvedMember: () => diagnostics.push(`enchantment-unresolved-member:${activeEnchantmentItem}`),
    }, true);
    function intrinsicEnchantment(row: Row): Row {
        const applied = readers.layerValue(row, 'enchantment_template_applied');
        if (!phpJsonTruthy(applied)) return row;
        const previous = activeEnchantmentItem;
        activeEnchantmentItem = phpJsonString(readItemField(row, 'item_name'));
        try {
            return applyFromBaseIntrinsicEnchantment(row, applied, { ...ownReaders,
                hasLayer: (host, key) => ownReaders.layerValue(host, key) !== null || phpJsonTruthy(ownReaders.layerEntries(host, key)), buildTemplate,
                charges: (holder, item) => { throw new FromBaseInputMissing('charges', `${holder}:${item}`); } }, metadata.dynamicLayers) as Row;
        } finally { activeEnchantmentItem = previous; }
    }
    function canonicalSize(row: Row): void {
        const size = canonicalizeCreatureSizeCells(Object.values(readers.layerEntries(row, 'size')));
        if (size) (row.item_layer_types as Row).size = metadata.layerCatalog.size?.is_multiple ? [size] : size;
    }
    /** merge_dynamic_classification's remaining lock/material window. Taxonomy
     * is resolved by the shared walker, never flattened onto the creature. */
    function dynamicClassification(source: Row): Row {
        let row: Row = { ...source, item_layer_types: { ...source.item_layer_types as Row } };
        for (const name of ['lock_type', 'item_composition']) {
            const nested: Row = {};
            for (const [slot, entry] of Object.entries(readers.layerEntries(row, name))) {
                const id = readLayerListEntryUuid(entry);
                if (!fromBaseUuid(id)) continue;
                const child = metadata.layerCatalog[name]?.full_nest ? definition(id) : rawDefinition(id);
                if (!child) continue;
                if (metadata.layerCatalog[name]?.full_nest) nested[slot] = child;
                else {
                    const cleaned = cleanEnvironmentalSource(child) as Row;
                    if (Object.prototype.hasOwnProperty.call(row, 'damage_type')
                        && Object.prototype.hasOwnProperty.call(cleaned, 'damage_type')) delete cleaned.damage_type;
                    row = appendEnvironmentalSource(row, cleaned) as Row;
                }
            }
            if (Object.keys(nested).length) row[name] = metadata.layerCatalog[name]?.is_multiple
                ? Object.keys(nested).every((key, index) => key === String(index)) ? Object.values(nested) : nested
                : Object.values(nested)[0];
        }
        if (readItemField(row, 'item_type') === 'creature') canonicalSize(row);
        if (!phpJsonTruthy(readers.layerValue(row, 'size'))) (row.item_layer_types as Row).size = TINY_CREATURE_SIZE_ID;
        // ft_apply_object_vitality also runs on taxonomy packages and creatures.
        const maximum = objectVitalityMax(row, readers, size => {
            const id = readLayerListEntryUuid(size);
            const sizeRow = metadata.sizeCatalog.find(entry => readItemField(entry, 'item_id') === id
                || readItemField(entry, 'variable_name') === id);
            return readItemField(sizeRow ?? null, 'vitality_scale') ?? vitalityScaleOf(id);
        }, definition);
        if (maximum !== null) {
            const computed = fromBaseRecord(row.computed) ? row.computed : {};
            row.computed = { ...computed, vitality: { ...(fromBaseRecord(computed.vitality) ? computed.vitality : {}),
                v_max: maximum, v_current: maximum } };
        }
        for (const name of ['creature_type', 'creature_sub_type', 'creature_template']) {
            if (!Array.isArray(row[name])) continue;
            const seen = new Set<string>();
            row[name] = row[name].filter(entry => {
                const id = readLayerListEntryUuid(entry);
                if (!id) return true;
                if (seen.has(id)) return false;
                seen.add(id); return true;
            });
        }
        return row;
    }
    return { readers, ownReaders, rawDefinition, definition, dynamicClassification, intrinsicEnchantment,
        canonicalSize, classificationTypes, diagnostics };
}
